import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from '../../translations/useTranslation';
import {
  Box,
  Flex,
  Text,
  Icon,
  Heading,
  SimpleGrid,
  Skeleton,
  Badge,
  Tooltip,
  HStack,
  VStack,
  Divider,
  useColorModeValue
} from '@chakra-ui/react';
import { 
  WiDaySunny, 
  WiRain, 
  WiCloudy, 
  WiSnow, 
  WiThunderstorm, 
  WiFog, 
  WiDayCloudy,
  WiHumidity, 
  WiBarometer,
  WiStrongWind,
  WiRaindrops,
  WiSunrise,
  WiSunset,
  WiTime1
} from 'react-icons/wi';
import { MdLocationOn, MdOutlineWaterDrop } from 'react-icons/md';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { OPEN_WEATHER_API_KEY } from '../../config/api_keys';
import './Weather.css';

const EnhancedWeatherForecast = () => {
  const { t } = useTranslation();
  const [forecast, setForecast] = useState(null);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);

  const API_KEY = OPEN_WEATHER_API_KEY;

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Use geolocation to get user's position
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setLocation({ lat: latitude, lng: longitude });
            
            // Get current weather
            const currentWeatherUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&units=metric&appid=${API_KEY}`;
            
            // Get 5-day forecast
            const forecastUrl = `https://api.openweathermap.org/data/2.5/forecast?lat=${latitude}&lon=${longitude}&units=metric&appid=${API_KEY}`;
            
            try {
              const [currentResponse, forecastResponse] = await Promise.all([
                axios.get(currentWeatherUrl),
                axios.get(forecastUrl)
              ]);
              
              setCurrentWeather(currentResponse.data);
              
              // Filter forecast to get one entry per day (at noon)
              const dailyForecasts = [];
              const forecastData = forecastResponse.data.list;
              
              // Get the date for tomorrow
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(0, 0, 0, 0);
              
              // Find one forecast per day
              const uniqueDays = {};
              
              forecastData.forEach(forecast => {
                const date = new Date(forecast.dt * 1000);
                const dateStr = date.toISOString().split('T')[0];
                
                // Only consider forecasts for future days (not today)
                if (date >= tomorrow) {
                  // If we haven't added this day yet or if this forecast is closer to noon
                  if (!uniqueDays[dateStr] || 
                      Math.abs(date.getHours() - 12) < Math.abs(new Date(uniqueDays[dateStr].dt * 1000).getHours() - 12)) {
                    uniqueDays[dateStr] = forecast;
                  }
                }
              });
              
              // Convert object to array and sort by date
              dailyForecasts.push(...Object.values(uniqueDays).slice(0, 5));
              
              setForecast(dailyForecasts);
              setLoading(false);
            } catch (err) {
              setError('Error fetching weather data');
              setLoading(false);
            }
          },
          (error) => {
            setError('Location access denied');
            setLoading(false);
          }
        );
      } catch (err) {
        setError('Error fetching weather data');
        setLoading(false);
      }
    };

    fetchWeather();
  }, [API_KEY]);

  // Function to get the appropriate weather icon
  const getWeatherIcon = (weatherCode) => {
    if (!weatherCode) return WiCloudy;
    
    // Weather condition codes: https://openweathermap.org/weather-conditions
    if (weatherCode >= 200 && weatherCode < 300) return WiThunderstorm;
    if (weatherCode >= 300 && weatherCode < 400) return WiRain;
    if (weatherCode >= 500 && weatherCode < 600) return WiRain;
    if (weatherCode >= 600 && weatherCode < 700) return WiSnow;
    if (weatherCode >= 700 && weatherCode < 800) return WiFog;
    if (weatherCode === 800) return WiDaySunny;
    if (weatherCode > 800) return WiDayCloudy;
    
    return WiCloudy; // Default icon
  };

  // Function to format time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString(t('locale'), { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true 
    }).toLowerCase();
  };

  if (loading) {
    return (
      <Box 
        p={4} 
        borderRadius="xl" 
        boxShadow="sm" 
        bg="white"
        w="100%"
      >
        <Skeleton height="180px" />
      </Box>
    );
  }

  if (error || !currentWeather || !forecast) {
    return (
      <Box 
        p={4} 
        borderRadius="xl" 
        boxShadow="sm"
        bg="white"
        w="100%"
      >
        <Text color="red.500">{error || t('unableToLoadWeather')}</Text>
      </Box>
    );
  }

  const CurrentWeatherIcon = getWeatherIcon(currentWeather.weather[0].id);

  return (
    <Box className="weather-forecast-container-compact">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" fontWeight="bold" color="green.700">
          {t('agriculturalWeatherForecast')}
        </Heading>
        {location && (
          <Flex align="center" bg="green.50" px={3} py={1} borderRadius="full">
            <Icon as={MdLocationOn} mr={1} boxSize={4} color="green.600" />
            <Text fontSize="sm" fontWeight="medium" color="green.700">
              {currentWeather.name}, {currentWeather.sys.country}
            </Text>
          </Flex>
        )}
      </Flex>
      
      {/* Current weather - Compact */}
      <Box p={4} borderRadius="xl" className="current-conditions-compact" mb={4}>
        <Flex justify="space-between" align="center">
          {/* Left side - Temperature and conditions */}
          <Flex align="center">
            <Icon as={CurrentWeatherIcon} color="green.500" boxSize={12} mr={3} />
            <Box>
              <Flex align="baseline">
                <Text fontSize="3xl" fontWeight="bold" color="gray.800">
                  {Math.round(currentWeather.main.temp)}°C
                </Text>
                <Box ml={2}>
                  <Flex align="center">
                    <Icon as={IoIosArrowUp} color="red.500" boxSize={3} />
                    <Text fontSize="sm" ml={1} color="red.500" fontWeight="medium">
                      {Math.round(currentWeather.main.temp_max)}°
                    </Text>
                  </Flex>
                  <Flex align="center">
                    <Icon as={IoIosArrowDown} color="blue.500" boxSize={3} />
                    <Text fontSize="sm" ml={1} color="blue.500" fontWeight="medium">
                      {Math.round(currentWeather.main.temp_min)}°
                    </Text>
                  </Flex>
                </Box>
              </Flex>
              <Text fontSize="md" mt={1} fontWeight="medium" color="gray.600" textTransform="capitalize">
                {currentWeather.weather[0].description}
              </Text>
            </Box>
          </Flex>

          {/* Right side - Weather details */}
          <SimpleGrid columns={2} spacing={4}>
            <VStack align="center" spacing={1}>
              <Flex align="center">
                <Icon as={WiHumidity} color="blue.400" boxSize={4} mr={1} />
                <Text fontSize="xs" color="gray.500">{t('humidity')}</Text>
              </Flex>
              <Text fontSize="md" fontWeight="bold" color="gray.800">{currentWeather.main.humidity}%</Text>
            </VStack>
            
            <VStack align="center" spacing={1}>
              <Flex align="center">
                <Icon as={WiStrongWind} color="green.400" boxSize={4} mr={1} />
                <Text fontSize="xs" color="gray.500">{t('wind')}</Text>
              </Flex>
              <Text fontSize="md" fontWeight="bold" color="gray.800">{Math.round(currentWeather.wind.speed * 3.6)} km/h</Text>
            </VStack>
          </SimpleGrid>
        </Flex>

        {/* Sunrise and Sunset - Compact */}
        <Flex justify="center" mt={4} pt={3} borderTop="1px solid" borderColor="gray.100">
          <HStack spacing={8}>
            <Flex align="center">
              <Icon as={WiSunrise} color="orange.400" boxSize={5} mr={2} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500">{t('sunrise')}</Text>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">{formatTime(currentWeather.sys.sunrise)}</Text>
              </VStack>
            </Flex>
            
            <Flex align="center">
              <Icon as={WiSunset} color="purple.400" boxSize={5} mr={2} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500">{t('sunset')}</Text>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">{formatTime(currentWeather.sys.sunset)}</Text>
              </VStack>
            </Flex>
          </HStack>
        </Flex>
      </Box>
      
    </Box>
  );
};

export default EnhancedWeatherForecast;